import React, { useEffect, useState } from 'react';
import { machineApi } from '../services/api';
import { Machine, MachineUsage } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { handleApiError, showSuccess } from '../utils/handleApiError';

const Machines: React.FC = () => {
  const { t } = useLanguage();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showUsageForm, setShowUsageForm] = useState<number | null>(null);
  const [newMachine, setNewMachine] = useState<Partial<Machine>>({
    name: '',
    type: '',
    manufacturer: '',
    model: '',
    license_plate: '',
    year_manufactured: undefined,
    purchase_price: undefined,
    operating_hours: 0,
    fuel_type: '',
    fuel_consumption_per_hour: undefined,
    service_interval_hours: undefined,
  });
  const [newUsage, setNewUsage] = useState<Partial<MachineUsage>>({
    usage_date: new Date().toISOString().split('T')[0],
    hours_used: 0,
    fuel_consumed: undefined,
    fuel_cost: undefined,
    activity: '',
    notes: '',
  });

  useEffect(() => {
    loadMachines();
  }, []);

  const loadMachines = async () => {
    try {
      setLoading(true);
      const response = await machineApi.getAll();
      setMachines(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading machines:', error);
      handleApiError(error, 'Hiba a gépek betöltésekor');
      setMachines([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMachine = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const machineData = {
        ...newMachine,
        year_manufactured: newMachine.year_manufactured ? parseInt(newMachine.year_manufactured.toString()) : undefined,
        purchase_price: newMachine.purchase_price ? parseFloat(newMachine.purchase_price.toString()) : undefined,
        fuel_consumption_per_hour: newMachine.fuel_consumption_per_hour ? parseFloat(newMachine.fuel_consumption_per_hour.toString()) : undefined,
        service_interval_hours: newMachine.service_interval_hours ? parseInt(newMachine.service_interval_hours.toString()) : undefined,
        operating_hours: newMachine.operating_hours || 0,
      };

      await machineApi.create(machineData);
      showSuccess('Gép sikeresen hozzáadva');
      setShowAddForm(false);
      setNewMachine({
        name: '',
        type: '',
        manufacturer: '',
        model: '',
        license_plate: '',
        year_manufactured: undefined,
        purchase_price: undefined,
        operating_hours: 0,
        fuel_type: '',
        fuel_consumption_per_hour: undefined,
        service_interval_hours: undefined,
      });
      loadMachines();
    } catch (error) {
      console.error('Error creating machine:', error);
      handleApiError(error, 'Hiba a gép létrehozásakor');
    }
  };

  const handleAddUsage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (showUsageForm === null) return;
    
    try {
      await machineApi.recordUsage(showUsageForm, {
        ...newUsage,
        hours_used: parseFloat(newUsage.hours_used?.toString() || '0'),
        fuel_consumed: newUsage.fuel_consumed ? parseFloat(newUsage.fuel_consumed.toString()) : undefined,
        fuel_cost: newUsage.fuel_cost ? parseFloat(newUsage.fuel_cost.toString()) : undefined,
      });
      
      showSuccess('Használat sikeresen rögzítve');
      setShowUsageForm(null);
      setNewUsage({
        usage_date: new Date().toISOString().split('T')[0],
        hours_used: 0,
        fuel_consumed: undefined,
        fuel_cost: undefined,
        activity: '',
        notes: '',
      });
      loadMachines();
    } catch (error) {
      console.error('Error recording usage:', error);
      handleApiError(error, 'Hiba a használat rögzítésekor');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  const getStatusColor = (machine: Machine) => {
    if (machine.is_service_due) return 'bg-red-100 text-red-800';
    if (machine.days_to_service !== undefined && machine.days_to_service <= 7) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusText = (machine: Machine) => {
    if (machine.is_service_due) return t('service_due', 'machines');
    if (machine.days_to_service !== undefined && machine.days_to_service <= 7) return `${machine.days_to_service} ${t('days', 'machines')}`;
    return t('adequate', 'machines');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('machines_management', 'machines')}</h1>
        <p className="mt-2 text-gray-600">
          {t('machines_management_desc', 'machines')}
        </p>
      </div>

      <div className="mb-6">
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
        >
          + {t('add', 'action')}
        </button>
      </div>

      {/* Add Machine Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('add_new_machine', 'machines')}</h3>
            <form onSubmit={handleAddMachine} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('name', 'machines')} *</label>
                  <input
                    type="text"
                    required
                    value={newMachine.name}
                    onChange={(e) => setNewMachine({ ...newMachine, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('type', 'machines')} *</label>
                  <select
                    required
                    value={newMachine.type}
                    onChange={(e) => setNewMachine({ ...newMachine, type: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  >
                    <option value="">{t('select_type', 'machines')}</option>
                    <option value="traktor">{t('tractor', 'machines')}</option>
                    <option value="kombajn">{t('combine', 'machines')}</option>
                    <option value="talajmuvelo">{t('soil_cultivator', 'machines')}</option>
                    <option value="permetezogep">{t('sprayer', 'machines')}</option>
                    <option value="vetogep">{t('seeder', 'machines')}</option>
                    <option value="arategep">{t('harvester', 'machines')}</option>
                    <option value="szallitogep">{t('transport_machine', 'machines')}</option>
                    <option value="egyeb">{t('other', 'machines')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('manufacturer', 'machines')}</label>
                  <input
                    type="text"
                    value={newMachine.manufacturer}
                    onChange={(e) => setNewMachine({ ...newMachine, manufacturer: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('model', 'machines')}</label>
                  <input
                    type="text"
                    value={newMachine.model}
                    onChange={(e) => setNewMachine({ ...newMachine, model: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('license_plate', 'machines')}</label>
                  <input
                    type="text"
                    value={newMachine.license_plate}
                    onChange={(e) => setNewMachine({ ...newMachine, license_plate: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('manufacturing_year', 'machines')}</label>
                  <input
                    type="number"
                    value={newMachine.year_manufactured || ''}
                    onChange={(e) => setNewMachine({ ...newMachine, year_manufactured: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('purchase_price_huf', 'machines')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newMachine.purchase_price || ''}
                    onChange={(e) => setNewMachine({ ...newMachine, purchase_price: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('operating_hours', 'machines')}</label>
                  <input
                    type="number"
                    value={newMachine.operating_hours}
                    onChange={(e) => setNewMachine({ ...newMachine, operating_hours: parseInt(e.target.value) || 0 })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('fuel_type', 'machines')}</label>
                  <select
                    value={newMachine.fuel_type}
                    onChange={(e) => setNewMachine({ ...newMachine, fuel_type: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  >
                    <option value="">{t('select_type', 'machines')}</option>
                    <option value="benzin">{t('gasoline', 'machines')}</option>
                    <option value="dizel">{t('diesel', 'machines')}</option>
                    <option value="elektromos">{t('electric', 'machines')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('consumption_per_hour', 'machines')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newMachine.fuel_consumption_per_hour || ''}
                    onChange={(e) => setNewMachine({ ...newMachine, fuel_consumption_per_hour: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('service_interval_hours', 'machines')}</label>
                  <input
                    type="number"
                    value={newMachine.service_interval_hours || ''}
                    onChange={(e) => setNewMachine({ ...newMachine, service_interval_hours: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {t('add', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Usage Form Modal */}
      {showUsageForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('record_usage', 'machines')}</h3>
            <form onSubmit={handleAddUsage} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('date', 'machines')} *</label>
                  <input
                    type="date"
                    required
                    value={newUsage.usage_date}
                    onChange={(e) => setNewUsage({ ...newUsage, usage_date: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('hours_used', 'machines')} *</label>
                  <input
                    type="number"
                    step="0.1"
                    required
                    value={newUsage.hours_used}
                    onChange={(e) => setNewUsage({ ...newUsage, hours_used: parseFloat(e.target.value) || 0 })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('fuel_consumed', 'machines')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newUsage.fuel_consumed || ''}
                    onChange={(e) => setNewUsage({ ...newUsage, fuel_consumed: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('fuel_cost_huf', 'machines')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newUsage.fuel_cost || ''}
                    onChange={(e) => setNewUsage({ ...newUsage, fuel_cost: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">{t('activity', 'machines')} *</label>
                  <input
                    type="text"
                    required
                    value={newUsage.activity}
                    onChange={(e) => setNewUsage({ ...newUsage, activity: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    placeholder={t('activity_placeholder', 'machines')}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">{t('notes', 'machines')}</label>
                  <textarea
                    value={newUsage.notes}
                    onChange={(e) => setNewUsage({ ...newUsage, notes: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowUsageForm(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {t('save', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('machines_list', 'machines')} ({machines.length})
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('machine', 'machines')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('type', 'machines')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('license_plate', 'machines')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('operating_hours', 'machines')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('service', 'machines')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('actions', 'machines')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {machines.map((machine) => (
                <tr key={machine.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {machine.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {machine.manufacturer} {machine.model}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{machine.type}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{machine.license_plate || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{machine.operating_hours} {t('hours', 'machines')}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(machine)}`}>
                      {getStatusText(machine)}
                    </span>
                    {machine.next_service_date && (
                      <div className="text-xs text-gray-500 mt-1">
                        {t('next_service', 'machines')}: {formatDate(machine.next_service_date)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => setShowUsageForm(machine.id)}
                      className="text-green-600 hover:text-green-900 mr-3"
                    >
                      {t('usage', 'action')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {machines.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 7.172V5L8 4z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('no_machines', 'machines')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('machines_will_appear_here', 'machines')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Machines;